import { DeleteOutlined, PlusOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Select, Space, Tag, Upload } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { messageService } from '../services/messageService';
import { ZaloTemplateCreateParam } from '../types/message';
import '../styles/AdminPages.css';

const { Option } = Select;
const { TextArea } = Input;

const ZaloTemplateCreatePage: React.FC = () => {
    const [createLoading, setCreateLoading] = useState(false);
    const [createForm] = Form.useForm();
    const [uploadingField, setUploadingField] = useState<string | null>(null);
    const [checkingPermission, setCheckingPermission] = useState(false);
    const [previewUrlByMediaId, setPreviewUrlByMediaId] = useState<Record<string, string>>({});
    const [previewUrlByField, setPreviewUrlByField] = useState<Record<string, string>>({});
    const navigate = useNavigate();

    const generateTrackingId = (length = 64) => {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const randomValues = new Uint32Array(length);
        window.crypto.getRandomValues(randomValues);
        return Array.from(randomValues, (value) => charset[value % charset.length]).join('');
    };

    const getTagOptionsByTemplateType = (templateType: 1 | 2 | 3 | 4 | 5) => {
        switch (templateType) {
            case 1:
                return [1, 2, 3];
            case 2:
            case 3:
                return [1];
            case 4:
                return [1, 2, 3];
            case 5:
                return [2];
            default:
                return [1];
        }
    };

    const getTemplateTypeLabel = (type: number) => {
        switch (type) {
            case 1: return 'Template tùy chỉnh';
            case 2: return 'Template xác thực';
            case 3: return 'Template yêu cầu thanh toán';
            case 4: return 'Template voucher';
            case 5: return 'Template đánh giá dịch vụ';
            default: return 'Không xác định';
        }
    };

    const getTagLabelFromValue = (tagValue?: number) => {
        switch (tagValue) {
            case 1: return 'Giao dịch';
            case 2: return 'Chăm sóc khách hàng';
            case 3: return 'Hậu mãi';
            default: return 'Không xác định';
        }
    };

    const setDefaultCreateFormValues = useCallback(() => {
        createForm.setFieldsValue({
            template_type: 1,
            tag: 1,
            tracking_id: generateTrackingId(),
            header_type: 'IMAGE',
            body_title: '',
            body_paragraph: '',
            table_rows: [],
            buttons: [],
            params: [],
            note: ''
        });
    }, [createForm]);

    const handleUploadMedia = async (file: File, fieldName: string) => {
        setUploadingField(fieldName);
        message.loading({ content: 'Đang upload ảnh...', key: 'zalo-template-upload', duration: 0 });
        try {
            const mediaId = await messageService.uploadZaloTemplateImage(file);
            createForm.setFieldValue(fieldName, mediaId);
            const previewUrl = URL.createObjectURL(file);
            setPreviewUrlByField((prev) => {
                const oldUrl = prev[fieldName];
                if (oldUrl) URL.revokeObjectURL(oldUrl);
                return { ...prev, [fieldName]: previewUrl };
            });
            setPreviewUrlByMediaId((prev) => ({ ...prev, [mediaId]: previewUrl }));
            message.success({ content: 'Upload ảnh thành công, đã điền media ID', key: 'zalo-template-upload' });
        } catch (error: any) {
            message.error({ content: error?.message || 'Không thể upload ảnh', key: 'zalo-template-upload' });
        } finally {
            setUploadingField(null);
        }
        return false;
    };

    const handleCreateTemplate = async (values: any) => {
        if (uploadingField) {
            message.warning('Đang upload ảnh, vui lòng đợi hoàn tất trước khi tạo template');
            return;
        }

        setCreateLoading(true);
        message.loading({ content: 'Đang tạo template...', key: 'zalo-template-create', duration: 0 });
        try {
            const templateType = Number(values.template_type) as 1 | 2 | 3 | 4 | 5;
            const tag = Number(values.tag) as 1 | 2 | 3;
            const allowedTags = getTagOptionsByTemplateType(templateType);
            if (!allowedTags.includes(tag)) {
                message.error({ content: 'Tag không hợp lệ với loại template đã chọn', key: 'zalo-template-create' });
                setCreateLoading(false);
                return;
            }

            const headerComponents: any[] = [];
            if (values.header_type === 'IMAGE' && values.header_image_media_id?.trim()) {
                headerComponents.push({
                    IMAGES: {
                        items: [{ type: 'IMAGE', media_id: values.header_image_media_id.trim() }]
                    }
                });
            }
            if (values.header_type === 'LOGO' && values.header_logo_light_media_id?.trim() && values.header_logo_dark_media_id?.trim()) {
                headerComponents.push({
                    LOGO: {
                        light: { type: 'IMAGE', media_id: values.header_logo_light_media_id.trim() },
                        dark: { type: 'IMAGE', media_id: values.header_logo_dark_media_id.trim() }
                    }
                });
            }

            const bodyComponents: any[] = [];
            if (values.body_title?.trim()) {
                bodyComponents.push({ TITLE: { value: values.body_title.trim() } });
            }
            if (values.body_paragraph?.trim()) {
                bodyComponents.push({ PARAGRAPH: { value: values.body_paragraph.trim() } });
            }

            const tableRows = (values.table_rows || [])
                .filter((row: any) => row?.title?.trim() && row?.value?.trim())
                .map((row: any) => ({
                    title: row.title.trim(),
                    value: row.value.trim(),
                    ...(row.row_type ? { row_type: Number(row.row_type) } : {})
                }));
            if (tableRows.length > 0) {
                bodyComponents.push({ TABLE: { rows: tableRows } });
            }

            const footerButtons = (values.buttons || [])
                .filter((button: any) => button?.type && button?.title?.trim() && button?.content?.trim())
                .map((button: any) => ({
                    type: Number(button.type),
                    title: button.title.trim(),
                    content: button.content.trim()
                }));

            const layout = {
                header: { components: headerComponents },
                body: { components: bodyComponents },
                footer: {
                    components: footerButtons.length > 0
                        ? [{ BUTTONS: { items: footerButtons } }]
                        : []
                }
            };

            const params = (values.params || [])
                .filter((param: any) => param?.type && param?.name?.trim() && param?.sample_value?.trim())
                .map((param: any) => ({
                    type: String(param.type),
                    name: param.name.trim(),
                    sample_value: param.sample_value.trim()
                })) as ZaloTemplateCreateParam[];

            await messageService.createZaloTemplate({
                template_name: values.template_name?.trim(),
                template_type: templateType,
                tag,
                layout,
                ...(params.length > 0 ? { params } : {}),
                ...(values.note?.trim() ? { note: values.note.trim() } : {}),
                tracking_id: values.tracking_id?.trim()
            });

            message.success({ content: 'Tạo template thành công, trạng thái hiện tại là Chờ duyệt', key: 'zalo-template-create' });
            navigate('/zalo-templates');
        } catch (error: any) {
            message.error({ content: error?.message || 'Không thể tạo template mới', key: 'zalo-template-create' });
        } finally {
            setCreateLoading(false);
        }
    };

    const handleCheckPermission = async () => {
        setCheckingPermission(true);
        message.loading({ content: 'Đang kiểm tra quyền OA...', key: 'zalo-template-check-permission', duration: 0 });
        try {
            const response = await messageService.checkZaloTemplatePermission();
            message.success({ content: 'OA đã có quyền sử dụng template resource', key: 'zalo-template-check-permission' });
            Modal.info({
                title: 'Kết quả kiểm tra quyền OA',
                width: 720,
                content: (
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {JSON.stringify(response, null, 2)}
                    </pre>
                )
            });
        } catch (error: any) {
            message.error({ content: error?.message || 'Kiểm tra quyền OA thất bại', key: 'zalo-template-check-permission' });
        } finally {
            setCheckingPermission(false);
        }
    };

    const buildPreviewData = (values: any) => {
        const headerImageMediaId = values.header_image_media_id?.trim();
        const headerLogoLightMediaId = values.header_logo_light_media_id?.trim();
        const headerLogoDarkMediaId = values.header_logo_dark_media_id?.trim();

        const tableRows = (values.table_rows || [])
            .filter((row: any) => row?.title?.trim() && row?.value?.trim())
            .map((row: any) => ({ title: row.title.trim(), value: row.value.trim() }));
        const buttons = (values.buttons || [])
            .filter((button: any) => button?.title?.trim() && button?.content?.trim())
            .map((button: any) => ({ type: Number(button.type), title: button.title.trim(), content: button.content.trim() }));
        return {
            templateName: values.template_name?.trim(),
            tagLabel: getTagLabelFromValue(Number(values.tag)),
            headerType: values.header_type,
            headerImageMediaId,
            headerLogoLightMediaId,
            headerLogoDarkMediaId,
            headerImagePreviewUrl: (headerImageMediaId && previewUrlByMediaId[headerImageMediaId]) || previewUrlByField.header_image_media_id,
            headerLogoLightPreviewUrl: (headerLogoLightMediaId && previewUrlByMediaId[headerLogoLightMediaId]) || previewUrlByField.header_logo_light_media_id,
            headerLogoDarkPreviewUrl: (headerLogoDarkMediaId && previewUrlByMediaId[headerLogoDarkMediaId]) || previewUrlByField.header_logo_dark_media_id,
            bodyTitle: values.body_title?.trim(),
            bodyParagraph: values.body_paragraph?.trim(),
            tableRows,
            buttons
        };
    };

    const renderPreviewParagraph = (text?: string) => {
        if (!text) return null;
        return text.split(/(<[^>]+>)/g).map((part: string, index: number) => {
            if (/^<[^>]+>$/.test(part)) {
                return <span key={`${part}-${index}`} style={{ fontWeight: 600, color: '#1d39c4' }}>{part}</span>;
            }
            return <span key={`${part}-${index}`}>{part}</span>;
        });
    };

    const watchedCreateValues = Form.useWatch([], createForm);
    const livePreviewData = buildPreviewData(watchedCreateValues || {});

    useEffect(() => {
        setDefaultCreateFormValues();
    }, [setDefaultCreateFormValues]);

    useEffect(() => {
        return () => {
            Object.values(previewUrlByField).forEach((url) => URL.revokeObjectURL(url));
        };
    }, [previewUrlByField]);

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1>Tạo template mới</h1>
                </div>
                <div className="admin-page-actions">
                    <Space>
                        <Button loading={checkingPermission} onClick={handleCheckPermission}>
                            Kiểm tra quyền OA
                        </Button>
                        <Button onClick={() => navigate('/zalo-templates')}>Quay lại danh sách</Button>
                        <Button type="primary" loading={createLoading || !!uploadingField} disabled={!!uploadingField} onClick={() => createForm.submit()}>
                            Tạo template
                        </Button>
                    </Space>
                </div>
            </div>

            <div className="admin-form-container" style={{ display: 'flex', gap: 16, height: 'calc(100vh - 180px)', minHeight: 0 }}>
                <div style={{ flex: 3.5, overflowY: 'auto', paddingRight: 10, minHeight: 0 }}>
                    <Form form={createForm} layout="vertical" onFinish={handleCreateTemplate}>
                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(280px, 1fr))', gap: 12 }}>
                                <Form.Item name="template_name" label="Tên mẫu tin" rules={[{ required: true, message: 'Vui lòng nhập tên mẫu tin' }, { min: 10, message: 'Tên mẫu tin tối thiểu 10 ký tự' }, { max: 60, message: 'Tên mẫu tin tối đa 60 ký tự' }]} style={{ marginBottom: 0 }}>
                                    <Input placeholder="Ví dụ: Mẫu chăm sóc khách hàng tháng 03" />
                                </Form.Item>

                                <Form.Item name="tracking_id" label="Tracking ID" rules={[{ required: true, message: 'Vui lòng nhập tracking ID' }, { max: 100, message: 'Tracking ID tối đa 100 ký tự' }]} style={{ marginBottom: 0 }}>
                                    <Input
                                        placeholder="Hệ thống tự tạo ngẫu nhiên theo dạng token"
                                        addonAfter={<Button type="link" size="small" icon={<SyncOutlined />} onClick={() => createForm.setFieldValue('tracking_id', generateTrackingId())} style={{ padding: 0, height: 'auto' }}>Random</Button>}
                                    />
                                </Form.Item>

                                <Form.Item name="template_type" label="Loại mẫu tin" rules={[{ required: true, message: 'Vui lòng chọn loại mẫu tin' }]} style={{ marginBottom: 0 }}>
                                    <Select onChange={(value: 1 | 2 | 3 | 4 | 5) => {
                                        const currentTag = createForm.getFieldValue('tag');
                                        const allowedTags = getTagOptionsByTemplateType(value);
                                        if (!allowedTags.includes(Number(currentTag))) createForm.setFieldValue('tag', allowedTags[0]);
                                    }}>
                                        <Option value={1}>{getTemplateTypeLabel(1)}</Option>
                                        <Option value={2}>{getTemplateTypeLabel(2)}</Option>
                                        <Option value={3}>{getTemplateTypeLabel(3)}</Option>
                                        <Option value={4}>{getTemplateTypeLabel(4)}</Option>
                                        <Option value={5}>{getTemplateTypeLabel(5)}</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item shouldUpdate={(p, c) => p.template_type !== c.template_type} noStyle>
                                    {() => {
                                        const templateType = (createForm.getFieldValue('template_type') || 1) as 1 | 2 | 3 | 4 | 5;
                                        const allowedTags = getTagOptionsByTemplateType(templateType);
                                        return (
                                            <Form.Item name="tag" label="Nhóm nội dung (Tag)" rules={[{ required: true, message: 'Vui lòng chọn nhóm nội dung' }]} style={{ marginBottom: 0 }}>
                                                <Select>
                                                    {allowedTags.includes(1) && <Option value={1}>Giao dịch</Option>}
                                                    {allowedTags.includes(2) && <Option value={2}>Chăm sóc khách hàng</Option>}
                                                    {allowedTags.includes(3) && <Option value={3}>Hậu mãi</Option>}
                                                </Select>
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>

                                <Form.Item name="note" label="Ghi chú kiểm duyệt" rules={[{ max: 400, message: 'Ghi chú tối đa 400 ký tự' }]} style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                                    <TextArea rows={2} placeholder="Mô tả ngắn mục đích sử dụng template để đội ngũ kiểm duyệt dễ theo dõi" />
                                </Form.Item>
                            </div>
                        </div>

                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Khai báo nội dung template</h3>
                            <Form.Item name="header_type" label="Kiểu phần đầu (Header)" rules={[{ required: true, message: 'Vui lòng chọn kiểu header' }]}>
                                <Select>
                                    <Option value="IMAGE">Ảnh</Option>
                                    <Option value="LOGO">Logo sáng/tối</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item shouldUpdate={(prev, next) => prev.header_type !== next.header_type} noStyle>
                                {() => {
                                    const headerType = createForm.getFieldValue('header_type');
                                    if (headerType === 'LOGO') {
                                        return (
                                            <Space style={{ width: '100%' }} size={16}>
                                                <Form.Item name="header_logo_light_media_id" label="Media ID logo sáng" rules={[{ required: true, message: 'Vui lòng nhập media ID logo sáng' }]} style={{ minWidth: 320, flex: 1 }}>
                                                    <Input placeholder="Nhập media_id của logo light" addonAfter={<Upload showUploadList={false} accept="image/*" beforeUpload={(file) => handleUploadMedia(file as File, 'header_logo_light_media_id')}><Button type="link" size="small" icon={<UploadOutlined />} loading={uploadingField === 'header_logo_light_media_id'} style={{ padding: 0, height: 'auto' }}>Tải ảnh</Button></Upload>} />
                                                </Form.Item>
                                                <Form.Item name="header_logo_dark_media_id" label="Media ID logo tối" rules={[{ required: true, message: 'Vui lòng nhập media ID logo tối' }]} style={{ minWidth: 320, flex: 1 }}>
                                                    <Input placeholder="Nhập media_id của logo dark" addonAfter={<Upload showUploadList={false} accept="image/*" beforeUpload={(file) => handleUploadMedia(file as File, 'header_logo_dark_media_id')}><Button type="link" size="small" icon={<UploadOutlined />} loading={uploadingField === 'header_logo_dark_media_id'} style={{ padding: 0, height: 'auto' }}>Tải ảnh</Button></Upload>} />
                                                </Form.Item>
                                            </Space>
                                        );
                                    }
                                    return (
                                        <Form.Item name="header_image_media_id" label="Media ID hình ảnh header" rules={[{ required: true, message: 'Vui lòng nhập media ID hình ảnh' }]}>
                                            <Input placeholder="Nhập media_id hình ảnh đã upload lên Zalo" addonAfter={<Upload showUploadList={false} accept="image/*" beforeUpload={(file) => handleUploadMedia(file as File, 'header_image_media_id')}><Button type="link" size="small" icon={<UploadOutlined />} loading={uploadingField === 'header_image_media_id'} style={{ padding: 0, height: 'auto' }}>Tải ảnh</Button></Upload>} />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>

                            <div style={{ marginTop: -8, marginBottom: 12, color: '#8c8c8c' }}>
                                Bạn có thể bấm "Tải ảnh" để hệ thống upload ảnh lên Zalo và tự điền media ID.
                            </div>

                            <Form.Item name="body_title" label="Tiêu đề" rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }, { max: 65, message: 'Tiêu đề tối đa 65 ký tự' }]}>
                                <Input placeholder="Ví dụ: Thông báo đơn hàng đã xác nhận" />
                            </Form.Item>

                            <Form.Item
                                name="body_paragraph"
                                label="Văn bản nội dung"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập văn bản nội dung' },
                                    { max: 400, message: 'Nội dung tối đa 400 ký tự' }
                                ]}
                                extra="Bạn có thể dùng biến dạng <ten_khach_hang>, <ma_don_hang>..."
                            >
                                <TextArea rows={4} placeholder="Nhập nội dung chính của template" />
                            </Form.Item>

                            <Form.List name="table_rows">
                                {(fields, { add, remove }) => (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600 }}>Bảng dữ liệu (Table)</span>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>Thêm dòng</Button>
                                        </div>
                                        {fields.map((field) => (
                                            <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                                                <Form.Item name={[field.name, 'title']} label="Nhãn" style={{ width: 200 }}>
                                                    <Input placeholder="Ví dụ: Mã đơn" />
                                                </Form.Item>
                                                <Form.Item name={[field.name, 'value']} label="Giá trị" style={{ width: 280 }}>
                                                    <Input placeholder="Ví dụ: <order_code>" />
                                                </Form.Item>
                                                <Form.Item name={[field.name, 'row_type']} label="Loại dòng" style={{ width: 140 }}>
                                                    <Select allowClear placeholder="Mặc định">
                                                        <Option value={1}>Nhấn mạnh</Option>
                                                        <Option value={2}>Thường</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} style={{ marginTop: 30 }} />
                                            </Space>
                                        ))}
                                    </div>
                                )}
                            </Form.List>

                            <Form.List name="buttons">
                                {(fields, { add, remove }) => (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600 }}>Nút thao tác (CTA)</span>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>Thêm nút</Button>
                                        </div>
                                        {fields.map((field) => (
                                            <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Form.Item name={[field.name, 'type']} style={{ width: 220, marginBottom: 0 }} rules={[{ required: true, message: 'Chọn loại nút' }]}>
                                                    <Select placeholder="Loại nút">
                                                        <Option value={1}>1 - Đến trang doanh nghiệp</Option>
                                                        <Option value={2}>2 - Gọi điện</Option>
                                                        <Option value={3}>3 - Trang OA</Option>
                                                        <Option value={4}>4 - Zalo Mini App</Option>
                                                        <Option value={5}>5 - Tải ứng dụng</Option>
                                                        <Option value={6}>6 - Phân phối sản phẩm</Option>
                                                        <Option value={7}>7 - Trang web/ZMA khác</Option>
                                                        <Option value={8}>8 - Ứng dụng khác</Option>
                                                        <Option value={9}>9 - Bài viết OA</Option>
                                                        <Option value={10}>10 - Sao chép</Option>
                                                        <Option value={11}>11 - Thanh toán ngay</Option>
                                                        <Option value={12}>12 - Xem chi tiết</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item name={[field.name, 'title']} style={{ width: 220, marginBottom: 0 }} rules={[{ required: true, message: 'Nhập tiêu đề nút' }]}>
                                                    <Input placeholder="Tiêu đề nút" />
                                                </Form.Item>
                                                <Form.Item name={[field.name, 'content']} style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, message: 'Nhập nội dung nút' }]}>
                                                    <Input placeholder="URL hoặc số điện thoại theo loại nút" />
                                                </Form.Item>
                                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.List>

                            <Form.List name="params">
                                {(fields, { add, remove }) => (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600 }}>Tham số động (Params)</span>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()}>Thêm tham số</Button>
                                        </div>
                                        {fields.length === 0 && <div style={{ marginBottom: 8, color: '#8c8c8c' }}>Không bắt buộc. Chỉ thêm khi trong nội dung có biến như &lt;ten_khach_hang&gt;.</div>}
                                        {fields.map((field) => (
                                            <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Form.Item name={[field.name, 'name']} style={{ width: 220, marginBottom: 0 }} rules={[{ required: true, message: 'Nhập tên tham số' }]}>
                                                    <Input placeholder="Ví dụ: ten_khach_hang" />
                                                </Form.Item>
                                                <Form.Item name={[field.name, 'type']} style={{ width: 260, marginBottom: 0 }} rules={[{ required: true, message: 'Chọn loại tham số' }]}>
                                                    <Select placeholder="Loại dữ liệu">
                                                        <Option value="1">1 - Tên khách hàng</Option>
                                                        <Option value="2">2 - Số điện thoại</Option>
                                                        <Option value="3">3 - Địa chỉ</Option>
                                                        <Option value="4">4 - Mã số</Option>
                                                        <Option value="5">5 - Nhãn tùy chỉnh</Option>
                                                        <Option value="6">6 - Trạng thái giao dịch</Option>
                                                        <Option value="7">7 - Thông tin liên hệ</Option>
                                                        <Option value="8">8 - Giới tính / Danh xưng</Option>
                                                        <Option value="9">9 - Tên sản phẩm / Thương hiệu</Option>
                                                        <Option value="10">10 - Số lượng / Số tiền</Option>
                                                        <Option value="11">11 - Thời gian</Option>
                                                        <Option value="12">12 - OTP</Option>
                                                        <Option value="13">13 - URL</Option>
                                                        <Option value="14">14 - Tiền tệ (VNĐ)</Option>
                                                        <Option value="15">15 - Bank transfer note</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item name={[field.name, 'sample_value']} style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, message: 'Nhập dữ liệu mẫu' }]}>
                                                    <Input placeholder="Dữ liệu mẫu để kiểm duyệt" />
                                                </Form.Item>
                                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.List>
                        </div>
                    </Form>
                </div>

                <div style={{ width: 320, borderLeft: '1px solid #f0f0f0', paddingLeft: 12, overflowY: 'auto', minHeight: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 12 }}>Xem trước Template</div>
                    <div style={{ background: '#f0f2ff', padding: 12, borderRadius: 8, border: '1px solid #d6dcff' }}>
                        <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong style={{ fontSize: 14 }}>{livePreviewData?.templateName || 'Tên mẫu tin'}</strong>
                            <Tag color="blue">{livePreviewData?.tagLabel || 'Tag'}</Tag>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e8e8e8', padding: 14 }}>
                            {livePreviewData?.headerType && (
                                <div style={{ marginBottom: 10 }}>
                                    {livePreviewData.headerType === 'IMAGE' ? (
                                        livePreviewData?.headerImagePreviewUrl ? (
                                            <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #e8e8e8' }}>
                                                <img
                                                    src={livePreviewData.headerImagePreviewUrl}
                                                    alt="Header preview"
                                                    style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', padding: 8, borderRadius: 6, fontSize: 11 }}>
                                                Header ảnh (media_id): {livePreviewData?.headerImageMediaId || '(chưa chọn)'}<br />
                                                Chưa có ảnh xem trước cho media_id này.
                                            </div>
                                        )
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                            <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', padding: 8, borderRadius: 6, fontSize: 11 }}>
                                                <div style={{ marginBottom: 6, fontWeight: 600 }}>Logo sáng</div>
                                                {livePreviewData?.headerLogoLightPreviewUrl ? (
                                                    <img
                                                        src={livePreviewData.headerLogoLightPreviewUrl}
                                                        alt="Logo light preview"
                                                        style={{ width: '100%', maxHeight: 70, objectFit: 'contain', background: '#fff' }}
                                                    />
                                                ) : (
                                                    <span>{livePreviewData?.headerLogoLightMediaId || '(chưa chọn)'}</span>
                                                )}
                                            </div>
                                            <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', padding: 8, borderRadius: 6, fontSize: 11 }}>
                                                <div style={{ marginBottom: 6, fontWeight: 600 }}>Logo tối</div>
                                                {livePreviewData?.headerLogoDarkPreviewUrl ? (
                                                    <img
                                                        src={livePreviewData.headerLogoDarkPreviewUrl}
                                                        alt="Logo dark preview"
                                                        style={{ width: '100%', maxHeight: 70, objectFit: 'contain', background: '#fff' }}
                                                    />
                                                ) : (
                                                    <span>{livePreviewData?.headerLogoDarkMediaId || '(chưa chọn)'}</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div style={{ fontSize: 24, color: '#1d39c4', fontWeight: 700, lineHeight: 1, marginBottom: 8 }}>Zalo</div>
                            <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>{livePreviewData?.bodyTitle || 'Tiêu đề template'}</div>
                            <div style={{ marginBottom: 12, whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 14 }}>
                                {renderPreviewParagraph(livePreviewData?.bodyParagraph) || 'Nội dung sẽ hiển thị tại đây'}
                            </div>
                            {livePreviewData?.tableRows?.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    {livePreviewData.tableRows.map((row: any, index: number) => (
                                        <div key={`${row.title}-${index}`} style={{ display: 'flex', marginBottom: 6, fontSize: 13 }}>
                                            <div style={{ minWidth: 110, color: '#595959' }}>{row.title}</div>
                                            <div style={{ fontWeight: 600 }}>{row.value}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {livePreviewData?.buttons?.length > 0 ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {livePreviewData.buttons.map((button: any, index: number) => (
                                        <Button key={`${button.title}-${index}`} type="primary" block>{button.title}</Button>
                                    ))}
                                </Space>
                            ) : (
                                <div style={{ color: '#8c8c8c', fontSize: 12 }}>Chưa có nút thao tác trong template.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ZaloTemplateCreatePage;
